import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
  type QueryKey,
} from '@tanstack/react-query'
import { sendCommand } from '../lib/extension'
import { db, type CachedInvite } from '../lib/db'
import { categorizeByHeadline } from '../lib/categorize'
import { toAppError, isAborted } from '../lib/errors'
import { createLogger } from '../lib/logger'
import { useAuth } from '../lib/auth'

const log = createLogger('invites')

export interface Invite {
  name: string
  headline: string
  sentTimeLabel: string | null
  invitationId: string | null
  sharedSecret: string | null
  entityUrn: string | null
  // The PERSON's profile URN (urn:li:fsd_profile:...). Distinct from entityUrn
  // which is the INVITATION's URN. Used for CRM matching on accept.
  profileUrn: string | null
  profileImage: string | null
  mutualConnections: string | null
  profileUrl: string | null
  message?: string
  category: string
}

const PAGE_SIZE = 100
const REFRESH_INTERVAL = 60_000
const MAX_SCAN_COUNT = 500

export { PERSON_CATEGORIES as INVITE_CATEGORIES } from '../lib/categorize'

// User-scoped: invites come from the extension (LinkedIn session for whoever
// is currently logged in), not from Supabase — there's no RLS backstop. If
// two users share a browser and we key on `['invites']` alone, user B briefly
// sees user A's cached invites after account switch.
export const invitesKey = (userId: string | undefined): QueryKey =>
  ['invites', userId ?? 'anon']

interface InvitesPage {
  invites: Invite[]
  start: number
}

function normalizeInvite<T extends Partial<Invite>>(raw: T): Invite {
  const inv = { ...raw } as Invite
  // Always re-categorize (in case categorization logic changed since caching).
  inv.category = categorizeByHeadline(inv.headline || '')
  return inv
}

async function fetchInvitesPage(start: number, signal: AbortSignal): Promise<Invite[]> {
  const result = await sendCommand<{ invites: Invite[] }>(
    'FETCH_INVITATIONS',
    { count: PAGE_SIZE, start },
    { signal },
  )
  return (Array.isArray(result?.invites) ? result.invites : []).map(normalizeInvite)
}

/**
 * Inbox for incoming LinkedIn invites.
 *
 * TanStack Query's `useInfiniteQuery` owns pagination state; Dexie acts as a
 * read-through cache for instant boot. Cancellation is plumbed via real
 * `AbortSignal`s that reach `sendCommand`, so filter-change → progressive
 * scan → category-back-to-ALL cleanly aborts in-flight fetches instead of
 * letting them land into stale state.
 */
export function useInvites() {
  const qc = useQueryClient()
  const { user } = useAuth()
  const userId = user?.id
  const [activeCategory, setActiveCategory] = useState<string>('ALL')
  const [scanning, setScanning] = useState(false)
  const [scanComplete, setScanComplete] = useState(false)
  const scanAbortRef = useRef<AbortController | null>(null)

  const query = useInfiniteQuery<InvitesPage, Error, InfiniteData<InvitesPage>, QueryKey, number>({
    queryKey: invitesKey(userId),
    enabled: !!userId,
    initialPageParam: 0,
    // Invites come from LinkedIn via the extension — we don't want react-query
    // to auto-refetch on window focus because the 60s interval below handles
    // staleness and a focus refetch would race with it.
    staleTime: REFRESH_INTERVAL,
    refetchOnWindowFocus: false,
    getNextPageParam: (lastPage) => {
      if (lastPage.invites.length < PAGE_SIZE) return undefined
      return lastPage.start + lastPage.invites.length
    },
    queryFn: async ({ pageParam, signal }) => {
      const start = pageParam as number
      const invites = await fetchInvitesPage(start, signal)
      return { invites, start }
    },
  })

  // ─── Dexie hydration ─────────────────────────────────────────────────────
  // On first mount, seed the query cache with whatever's in Dexie so the UI
  // has something instantly. The network fetch happens in parallel and
  // overwrites when it lands (standard stale-while-revalidate).
  const hydratedRef = useRef(false)
  useEffect(() => {
    if (hydratedRef.current) return
    hydratedRef.current = true
    ;(async () => {
      try {
        const cached = await db.invites.toArray()
        if (cached.length === 0) return
        const seen = new Set<string>()
        const deduped = cached.filter((i) => {
          const k = String(i.invitationId)
          if (seen.has(k)) return false
          seen.add(k)
          return true
        })
        // Only seed if TQ hasn't populated yet. If the API returned before
        // this microtask resolves, skip — live data wins.
        const current = qc.getQueryData<InfiniteData<InvitesPage>>(invitesKey(userId))
        if (current && current.pages.length > 0) return
        qc.setQueryData<InfiniteData<InvitesPage>>(invitesKey(userId), {
          pages: [{ invites: deduped.map(normalizeInvite), start: 0 }],
          pageParams: [0],
        })
      } catch (err) {
        log.warn('Dexie hydration failed', err)
      }
    })()
  }, [qc, userId])

  // ─── Dexie write-through ─────────────────────────────────────────────────
  // After every successful fetch, mirror the data into Dexie so a future
  // page load boots from cache. The clear() + bulkPut() pair runs inside a
  // single Dexie transaction so a crash (tab close, quota exceeded) between
  // the two can't leave the cache empty. Dexie aborts the transaction on
  // error and rolls back the clear().
  useEffect(() => {
    if (!query.data) return
    const flat = query.data.pages.flatMap((p) => p.invites)
    if (flat.length === 0) return
    ;(async () => {
      try {
        await db.transaction('rw', db.invites, async () => {
          await db.invites.clear()
          const toCache: CachedInvite[] = flat
            .filter((i) => i.invitationId)
            .map((i) => ({ ...i, invitationId: i.invitationId!, _cachedAt: Date.now() }))
          if (toCache.length > 0) await db.invites.bulkPut(toCache)
        })
      } catch (err) {
        log.warn('Dexie write-through failed', err)
      }
    })()
  }, [query.data])

  // ─── Auto-refresh first page every 60s ───────────────────────────────────
  // Only refetch page 0 — if the user has progressively scanned deep
  // (potentially 500 invites across 5 pages) we don't want to re-download
  // every page every minute. New invites always land at the top of LinkedIn's
  // feed, so page 0 is sufficient to surface them. The rest of the pages
  // stay in-place and the infinite query's flat-join stitches everything
  // back together.
  useEffect(() => {
    if (!userId) return
    const id = setInterval(() => {
      void (async () => {
        try {
          const controller = new AbortController()
          const fresh = await fetchInvitesPage(0, controller.signal)
          qc.setQueryData<InfiniteData<InvitesPage>>(invitesKey(userId), (prev) => {
            if (!prev || prev.pages.length === 0) {
              return { pages: [{ invites: fresh, start: 0 }], pageParams: [0] }
            }
            // Replace just page 0 — preserve page 1+ so the user's
            // progressive scan doesn't reset.
            const rest = prev.pages.slice(1)
            return { ...prev, pages: [{ invites: fresh, start: 0 }, ...rest] }
          })
        } catch (err) {
          if (!isAborted(err)) log.warn('page-0 refresh failed', toAppError(err))
        }
      })()
    }, REFRESH_INTERVAL)
    return () => clearInterval(id)
  }, [qc, userId])

  // ─── Derived state ───────────────────────────────────────────────────────
  const allInvites = useMemo(
    () => (query.data?.pages ?? []).flatMap((p) => p.invites),
    [query.data],
  )

  const filteredInvites = useMemo(() => {
    if (activeCategory === 'ALL') return allInvites
    return allInvites.filter((inv) => inv.category === activeCategory)
  }, [allInvites, activeCategory])

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { ALL: allInvites.length }
    for (const inv of allInvites) {
      counts[inv.category] = (counts[inv.category] || 0) + 1
    }
    return counts
  }, [allInvites])

  // ─── Progressive scan (triggered when user picks a filter) ───────────────
  const startScan = useCallback(async () => {
    if (scanning || scanComplete) return
    // Tear down any previous scan controller.
    scanAbortRef.current?.abort()
    const controller = new AbortController()
    scanAbortRef.current = controller
    setScanning(true)

    try {
      while (!controller.signal.aborted) {
        const current = qc.getQueryData<InfiniteData<InvitesPage>>(invitesKey(userId))
        const loaded = current?.pages.reduce((n, p) => n + p.invites.length, 0) ?? 0
        if (loaded >= MAX_SCAN_COUNT) break
        const lastPage = current?.pages.at(-1)
        if (lastPage && lastPage.invites.length < PAGE_SIZE) break
        // Re-use TQ's own fetchNextPage so the cache update (and any
        // in-flight dedupe) lives in one place.
        const result = await query.fetchNextPage()
        if (controller.signal.aborted) break
        if (!result.data || result.data.pages.length === 0) break
        const latest = result.data.pages.at(-1)
        if (!latest || latest.invites.length === 0) break
        if (latest.invites.length < PAGE_SIZE) break
      }
      if (!controller.signal.aborted) setScanComplete(true)
    } catch (err) {
      if (!isAborted(err)) log.warn('scan failed', toAppError(err))
    } finally {
      setScanning(false)
    }
  }, [scanning, scanComplete, qc, query, userId])

  // Abort any running scan when switching back to ALL.
  const handleCategoryChange = useCallback((category: string) => {
    setActiveCategory(category)
    if (category === 'ALL') {
      scanAbortRef.current?.abort()
      return
    }
    if (!scanComplete && !scanning) startScan()
  }, [scanComplete, scanning, startScan])

  useEffect(() => () => { scanAbortRef.current?.abort() }, [])

  // ─── Respond to invite (accept/ignore) ───────────────────────────────────
  const respondMut = useMutation({
    mutationKey: ['invites', 'respond'],
    mutationFn: async ({ invite, action }: { invite: Invite; action: 'accept' | 'ignore' }) => {
      const nameParts = (invite.name || '').split(' ')
      const slug = (() => {
        if (!invite.profileUrl) return null
        const m = invite.profileUrl.match(/\/in\/([^/?#]+)/)
        return m ? decodeURIComponent(m[1]) : null
      })()
      const result = await sendCommand<{ status: string }>('RESPOND_INVITATION', {
        invitationId: invite.invitationId,
        action,
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        validationToken: invite.sharedSecret || '',
        // profileUrn is the PERSON's URN (urn:li:fsd_profile:...).
        // entityUrn is the INVITATION's URN — wrong type for CRM
        // matching. Previously we fell back to entityUrn when
        // profileUrn was null, which fed the invitation URN into the
        // CRM sync path as if it were a person key. The extension
        // now uses `strictProfileUrn` to reject non-profile URNs, so
        // sending null here is safe — it just skips CRM sync for
        // that accept, which is the correct behaviour when we don't
        // have a reliable person URN.
        // See POST_FIX_ASTAR_AUDIT_HANDOFF finding #2.
        profileUrn: invite.profileUrn,
        displayName: invite.name,
        headline: invite.headline,
        slug,
      })
      return { invite, status: result?.status }
    },
    // Optimistic remove from cache + Dexie
    onMutate: async ({ invite }) => {
      const key = invitesKey(userId)
      await qc.cancelQueries({ queryKey: key })
      const snapshot = qc.getQueryData<InfiniteData<InvitesPage>>(key)
      qc.setQueryData<InfiniteData<InvitesPage>>(key, (prev) => {
        if (!prev) return prev
        return {
          ...prev,
          pages: prev.pages.map((p) => ({
            ...p,
            invites: p.invites.filter((i) => i.invitationId !== invite.invitationId),
          })),
        }
      })
      if (invite.invitationId) {
        db.invites.delete(invite.invitationId).catch(() => { /* best-effort */ })
      }
      return { snapshot }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshot) qc.setQueryData(invitesKey(userId), ctx.snapshot)
    },
  })

  const respondToInvite = useCallback(
    (invite: Invite, action: 'accept' | 'ignore'): Promise<string> => {
      return respondMut
        .mutateAsync({ invite, action })
        .then(({ status }) => {
          if (status === 'already_handled') {
            log.warn('Invite already handled externally', invite.invitationId)
          }
          return invite.name
        })
    },
    [respondMut],
  )

  return {
    invites: filteredInvites,
    allInvites,
    loading: query.isPending,
    inviteCount: allInvites.length,
    activeCategory,
    setActiveCategory: handleCategoryChange,
    categoryCounts,
    scanning,
    scannedCount: allInvites.length,
    scanComplete,
    hasMore: !!query.hasNextPage,
    paginating: query.isFetchingNextPage,
    loadMore: () => {
      if (!query.isFetchingNextPage && query.hasNextPage) query.fetchNextPage()
    },
    respondToInvite,
  }
}
